import {ENTITY_CARD_SHAPE_TYPE} from '../tldraw/customShapes/entityCardConstants';

export const AI_AUTHOR_UID = 'ai';

export const AI_AUTHOR = Object.freeze({
    uid: AI_AUTHOR_UID,
    displayName: 'Minsky Assistant',
    isAI: true,
});

const CARD_CONTENT_KEYS = ['label', 'description', 'wikidataId'];

let kbHydrationDepth = 0;

export function beginKbHydration() {
    kbHydrationDepth += 1;
}

export function endKbHydration() {
    kbHydrationDepth = Math.max(0, kbHydrationDepth - 1);
}

export function isKbHydrationInProgress() {
    return kbHydrationDepth > 0;
}

export function isAuthorshipStampSuppressed() {
    return isKbHydrationInProgress();
}

export function isChartAuthoredShape(shape) {
    return shape?.type === ENTITY_CARD_SHAPE_TYPE || shape?.type === 'arrow';
}

/**
 * Last writer on a card or edge. Prefers `meta.authorship.userId`.
 * Legacy machine-review shapes (`meta.review.origin === 'machine'`) backfill as AI.
 *
 * @param {{meta?: object}|null|undefined} shape
 * @returns {string|null}
 */
export function getShapeAuthorId(shape) {
    const stamped = shape?.meta?.authorship?.userId;
    if (typeof stamped === 'string' && stamped.trim()) {
        return stamped.trim();
    }
    if (shape?.meta?.review?.origin === 'machine') {
        return AI_AUTHOR_UID;
    }
    return null;
}

export function mergeAuthorshipMeta(existingMeta, userId) {
    const base = existingMeta && typeof existingMeta === 'object' ? existingMeta : {};
    return {
        ...base,
        authorship: {userId},
    };
}

export function writeShapeAuthorship(editor, shape, userId) {
    if (!editor || !shape || !userId) return;
    if (shape.meta?.authorship?.userId === userId) return;
    editor.updateShapes?.([{
        id: shape.id,
        type: shape.type,
        meta: mergeAuthorshipMeta(shape.meta, userId),
    }]);
}

export function applyMachineAuthorshipToShape(editor, shapeId) {
    const shape = editor?.getShape?.(shapeId);
    if (!shape) return;
    writeShapeAuthorship(editor, shape, AI_AUTHOR_UID);
}

function propsChanged(prev, next, keys) {
    return keys.some((key) => {
        const a = prev?.props?.[key];
        const b = next?.props?.[key];
        if (a === b) return false;
        if (typeof a === 'object' || typeof b === 'object') {
            return JSON.stringify(a) !== JSON.stringify(b);
        }
        return true;
    });
}

/**
 * True when a local edit should retitle last-author (create is handled separately).
 * Move / resize / style / layout are ignored.
 */
export function isAuthorshipContentChange(prev, next) {
    if (!prev || !next || prev.id !== next.id) return false;
    if (!isChartAuthoredShape(next)) return false;
    if (next.type === ENTITY_CARD_SHAPE_TYPE) {
        return propsChanged(prev, next, CARD_CONTENT_KEYS);
    }
    return propsChanged(prev, next, ['text', 'richText', 'label']);
}

export function isHiddenChartShape(editor, shape) {
    if (!editor || !shape) return false;
    return Boolean(editor.isShapeHidden?.(shape.id) || editor.isShapeHidden?.(shape));
}

/**
 * Unique last-author ids on currently visible cards and edges.
 *
 * @param {object|null|undefined} editor
 * @returns {string[]}
 */
export function collectVisibleChartAuthorIds(editor) {
    const shapes = editor?.getCurrentPageShapes?.() || [];
    const ids = [];
    const seen = new Set();
    for (const shape of shapes) {
        if (!isChartAuthoredShape(shape) || isHiddenChartShape(editor, shape)) continue;
        const authorId = getShapeAuthorId(shape);
        if (!authorId || seen.has(authorId)) continue;
        seen.add(authorId);
        ids.push(authorId);
    }
    return ids;
}

export function selectedAuthorHasVisibleShape(editor, authorId) {
    if (!authorId) return false;
    const shapes = editor?.getCurrentPageShapes?.() || [];
    return shapes.some((shape) => (
        isChartAuthoredShape(shape)
        && !isHiddenChartShape(editor, shape)
        && getShapeAuthorId(shape) === authorId
    ));
}

export function collectAuthorHighlightArrows(editor, authorIds) {
    const selected = new Set((authorIds || []).filter(Boolean));
    if (selected.size === 0) return [];
    const shapes = editor?.getCurrentPageShapes?.() || [];
    const out = [];
    for (const shape of shapes) {
        if (shape?.type !== 'arrow' || isHiddenChartShape(editor, shape)) continue;
        const authorId = getShapeAuthorId(shape);
        if (!selected.has(authorId)) continue;
        out.push({id: shape.id, authorId});
    }
    return out;
}

export function sortChartAuthorIds(authorIds) {
    const unique = [...new Set((authorIds || []).filter(Boolean))];
    const humans = unique.filter((id) => id !== AI_AUTHOR_UID).sort((a, b) => a.localeCompare(b));
    if (unique.includes(AI_AUTHOR_UID)) {
        humans.push(AI_AUTHOR_UID);
    }
    return humans;
}

export function sameAuthorIdList(left, right) {
    const a = sortChartAuthorIds(left);
    const b = sortChartAuthorIds(right);
    return a.length === b.length && a.every((id, index) => id === b[index]);
}
